import type { SifaUnterweisungVorlage, UnterweisungStatus } from '../../../services/sifa/types';
import { vorlageMedienLoeschbar } from '../../../services/sifa/vorlagenLogic';
import { formatDatum, UnterweisungEntwurf, VORGABE_BESTEHEN_PROZENT, VORGABE_GUELTIG_MONATE } from '../unterweisungModulLogic';

/**
 * Die Regeln der Ansicht „Unterweisungen" (globale Vorlagen, Nr. 141,
 * docs/sifa-vorlagen-plan.md §3e) — ohne React, ohne Supabase. Wie bei
 * unterweisungModulLogic: kein jsdom, also steht jede Entscheidung der
 * Oberflaeche (was ist sichtbar, was darf geloescht werden, welcher Text)
 * hier und in vorlagenViewLogic.test.ts.
 */

// ─── Status ─────────────────────────────────────────────────────────────────

/** 'aktiv' heisst bei Vorlagen „Freigegeben" — beim Kunden ist ein Kurs „Aktiv". */
export const VORLAGEN_STATUS_LABEL: Record<UnterweisungStatus, string> = {
  entwurf: 'Entwurf',
  aktiv: 'Freigegeben',
  archiviert: 'Archiviert',
};

/** `aktuell` = alles ausser Archiv: das Archiv ist Ablage, keine Arbeitsliste. */
export type VorlagenStatusFilter = 'aktuell' | UnterweisungStatus | 'alle';

export const STATUS_FILTER_VORGABE: VorlagenStatusFilter = 'aktuell';

export const STATUS_FILTER_LABEL: Record<VorlagenStatusFilter, string> = {
  aktuell: 'Ohne Archiv',
  entwurf: 'Entwurf',
  aktiv: 'Freigegeben',
  archiviert: 'Archiviert',
  alle: 'Alle',
};

export const passtZumStatus = (v: Pick<SifaUnterweisungVorlage, 'status'>, filter: VorlagenStatusFilter): boolean => {
  if (filter === 'alle') return true;
  if (filter === 'aktuell') return v.status !== 'archiviert';
  return v.status === filter;
};

export const zaehleStatus = (liste: Pick<SifaUnterweisungVorlage, 'status'>[]): Record<VorlagenStatusFilter, number> => {
  const z: Record<VorlagenStatusFilter, number> = { aktuell: 0, entwurf: 0, aktiv: 0, archiviert: 0, alle: 0 };
  for (const v of liste || []) {
    z.alle += 1;
    z[v.status] += 1;
    if (v.status !== 'archiviert') z.aktuell += 1;
  }
  return z;
};

// ─── Suche, Kategorie, Gruppen ──────────────────────────────────────────────

/** Gruppe der Vorlagen ohne Kategorie — steht immer am Ende. */
export const OHNE_KATEGORIE = 'Ohne Kategorie';

/** Schluessel einer Kategorie: „Brandschutz" und „brandschutz " sind dieselbe. '' = ohne. */
const kategorieSchluessel = (k: string | null | undefined): string => (k ?? '').trim().toLocaleLowerCase('de');

const vergleiche = (a: string, b: string): number => a.localeCompare(b, 'de', { sensitivity: 'base' });

/** Mehrere Woerter = alle muessen vorkommen (wie sucheMitarbeiter), in Titel, Kategorie oder Beschreibung. */
export const sucheVorlagen = <T extends Pick<SifaUnterweisungVorlage, 'titel' | 'kategorie' | 'beschreibung'>>(
  liste: T[],
  suche: string,
): T[] => {
  const woerter = (suche || '').trim().toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return liste;
  return liste.filter((v) => {
    const text = `${v.titel || ''} ${v.kategorie || ''} ${v.beschreibung || ''}`.toLocaleLowerCase('de');
    return woerter.every((w) => text.includes(w));
  });
};

export interface VorlagenFilter {
  suche: string;
  status: VorlagenStatusFilter;
}

export const filtereVorlagen = <T extends Pick<SifaUnterweisungVorlage, 'titel' | 'kategorie' | 'beschreibung' | 'status'>>(
  liste: T[],
  filter: VorlagenFilter,
): T[] => sucheVorlagen((liste || []).filter((v) => passtZumStatus(v, filter.status)), filter.suche);

/**
 * Die Kategorien fuer die Vorschlagsliste im Editor: ohne Leere, ohne
 * Doppelte (Gross/Klein egal, die erste Schreibweise gewinnt), sortiert.
 * Ein Freitextfeld mit Vorschlaegen haelt die Schreibweisen zusammen, ohne
 * eine Stammdatenpflege fuer Kategorien zu verlangen.
 */
export const kategorienAus = (liste: Pick<SifaUnterweisungVorlage, 'kategorie'>[]): string[] => {
  const gesehen = new Map<string, string>();
  for (const v of liste || []) {
    const k = (v.kategorie ?? '').trim();
    const s = kategorieSchluessel(k);
    if (s && !gesehen.has(s)) gesehen.set(s, k);
  }
  return [...gesehen.values()].sort(vergleiche);
};

export interface VorlagenGruppe<T> {
  /** Schluessel (klein, getrimmt); '' = ohne Kategorie. */
  schluessel: string;
  label: string;
  vorlagen: T[];
}

/**
 * Gruppen nach Kategorie, alphabetisch, „Ohne Kategorie" zuletzt; in jeder
 * Gruppe nach Titel. Die Bezeichnung ist die erste Schreibweise in
 * Titelreihenfolge — stabil, auch wenn zwei Vorlagen sie verschieden tippen.
 */
export const gruppiereNachKategorie = <T extends Pick<SifaUnterweisungVorlage, 'titel' | 'kategorie'>>(
  liste: T[],
): VorlagenGruppe<T>[] => {
  const sortiert = [...(liste || [])].sort((a, b) => vergleiche(a.titel || '', b.titel || ''));
  const gruppen = new Map<string, VorlagenGruppe<T>>();
  for (const v of sortiert) {
    const schluessel = kategorieSchluessel(v.kategorie);
    let g = gruppen.get(schluessel);
    if (!g) {
      g = { schluessel, label: schluessel ? (v.kategorie ?? '').trim() : OHNE_KATEGORIE, vorlagen: [] };
      gruppen.set(schluessel, g);
    }
    g.vorlagen.push(v);
  }
  return [...gruppen.values()].sort((a, b) => {
    if (!a.schluessel !== !b.schluessel) return a.schluessel ? -1 : 1;
    return vergleiche(a.label, b.label);
  });
};

// ─── Aktionen je Zeile ──────────────────────────────────────────────────────

/**
 * Loeschen anbieten? Nur im Entwurf und nur ohne Hinweis aus
 * vorlageLoeschbarHinweis (Service: bekannte Kundenkopien). Das ist ein
 * Vorab-Filter — ob eine einmal freigegebene Vorlage wirklich frei ist,
 * entscheidet die Datenbank beim Loeschen (Nr. 144).
 */
export const darfLoeschen = (v: Pick<SifaUnterweisungVorlage, 'status'>, loeschHinweis: string | null): boolean =>
  v.status === 'entwurf' && loeschHinweis === null;

/**
 * Vorlagen anlegen, aendern, freigeben, archivieren, loeschen: Admins, Nutzer
 * mit Haekchen „SiFa" (Nr. 141) und — Rollensystem — wer das Recht
 * „SiFa-Vorlagen" hat. ODER, nicht statt: das Haekchen gilt weiter, bis es
 * abgeloest wird. Die Datenbank prueft dasselbe (Nr. 154).
 */
export const darfVorlagenBearbeiten = (istAdmin: boolean, istSifa: boolean, hatRecht: boolean): boolean =>
  istAdmin || istSifa || hatRecht;

export type ZeilenAktion = 'bearbeiten' | 'ansehen' | 'testen' | 'freigeben' | 'wiederFreigeben' | 'archivieren' | 'loeschen';

/**
 * Was die Liste je Vorlage anbietet. „Testen" hat jeder mit Zugriff (der
 * Test-Link schreibt nichts). Ohne Recht sonst nur „Ansehen". Ein Entwurf,
 * der schon einmal freigegeben war, behaelt „Archivieren" neben „Loeschen" —
 * lehnt die Datenbank das Loeschen ab, bleibt der Weg ins Archiv.
 */
export const zeilenAktionen = (
  v: Pick<SifaUnterweisungVorlage, 'status' | 'erstmalsFreigegebenAm'>,
  darfBearbeiten: boolean,
  loeschHinweis: string | null,
): ZeilenAktion[] => {
  if (!darfBearbeiten) return ['ansehen', 'testen'];
  switch (v.status) {
    case 'entwurf': {
      const aktionen: ZeilenAktion[] = ['bearbeiten', 'testen', 'freigeben'];
      if (darfLoeschen(v, loeschHinweis)) aktionen.push('loeschen');
      if (!vorlageMedienLoeschbar(v)) aktionen.push('archivieren');
      return aktionen;
    }
    case 'aktiv':
      return ['bearbeiten', 'testen', 'archivieren'];
    default:
      return ['ansehen', 'testen', 'wiederFreigeben'];
  }
};

// ─── Editor ─────────────────────────────────────────────────────────────────

/**
 * Der Editor-Entwurf aus einer Vorlage — dieselbe Normalisierung wie
 * entwurfAus fuer einen Kurs (der Test haelt beide gleich). Eigene Funktion,
 * weil entwurfAus eine vollstaendige SifaUnterweisung verlangt.
 */
export const entwurfAusVorlage = (v: SifaUnterweisungVorlage): UnterweisungEntwurf => ({
  titel: v.titel || '',
  beschreibung: v.beschreibung || '',
  inhalt: (v.inhalt || []).map((a) => ({ ...a })),
  fragen: (v.fragen || []).map((f) => ({ ...f, antworten: (f.antworten || []).map((a) => ({ ...a })) })),
  bestehenProzent: Number.isFinite(v.bestehenProzent) ? v.bestehenProzent : VORGABE_BESTEHEN_PROZENT,
  gueltigMonate: Number.isFinite(v.gueltigMonate) ? v.gueltigMonate : VORGABE_GUELTIG_MONATE,
  dauerMin: v.dauerMin ?? null,
});

/** Angaben unter der Ueberschrift: „Fassung 3", „zuletzt geändert von X am 07.10.2026". */
export const vorlageKopfAngaben = (v: Pick<SifaUnterweisungVorlage, 'fassung' | 'geaendertVonName' | 'updatedAt'>): string[] => {
  const wer = (v.geaendertVonName || '').trim();
  const wann = v.updatedAt ? formatDatum(v.updatedAt) : '';
  const angaben = [`Fassung ${v.fassung}`];
  if (wer || wann) angaben.push(`zuletzt geändert${wer ? ` von ${wer}` : ''}${wann ? ` am ${wann}` : ''}`);
  return angaben;
};

export const FREIGABE_HINWEIS =
  'Freigegeben: Medien bleiben erhalten, weil Kundenkopien darauf zeigen. Änderungen erzeugen eine neue Fassung, Kunden übernehmen sie per Knopf.';

/**
 * Hinweis im Kopf des Editors. Massgeblich ist die ERSTE Freigabe, nicht der
 * Status: auch zurueck im Entwurf koennen Kopien auf die Medien zeigen.
 * Archiviert sagt der Sperrhinweis genug.
 */
export const freigabeHinweis = (v: Pick<SifaUnterweisungVorlage, 'status' | 'erstmalsFreigegebenAm'>): string | null => {
  if (v.status === 'aktiv') return FREIGABE_HINWEIS;
  if (v.status === 'entwurf' && !vorlageMedienLoeschbar(v)) {
    return 'Schon einmal freigegeben: Medien bleiben erhalten, weil Kundenkopien darauf zeigen können. Löschen geht nur, solange nichts mehr auf die Vorlage verweist — das prüft die Datenbank.';
  }
  return null;
};

// ─── Texte (Liste und Editor sagen dasselbe) ────────────────────────────────

/** Vor der ERSTEN Freigabe: ab dann gibt es kein Zurueck zum Loeschen. */
export const FRAGE_ERSTE_FREIGABE =
  'Vorlage freigeben? Danach können alle SiFa-Kunden sie übernehmen. Sobald ein Kunde sie übernommen hat, lässt sie sich nicht mehr löschen, nur archivieren; ihre Bilder und Videos bleiben ab jetzt erhalten, weil Kundenkopien darauf zeigen.';

export const FRAGE_ARCHIVIEREN =
  'Vorlage archivieren? Beim Kunden steht sie dann nicht mehr zur Auswahl; bestehende Kundenkopien laufen unverändert weiter. „Wieder freigeben" holt sie zurück.';

export const FRAGE_ZURUECK_AUF_ENTWURF =
  'Zurück auf Entwurf? Beim Kunden steht die Vorlage dann nicht mehr zur Auswahl, bis sie wieder freigegeben ist; bestehende Kundenkopien laufen weiter.';

export const frageLoeschen = (v: Pick<SifaUnterweisungVorlage, 'titel' | 'erstmalsFreigegebenAm'>): string => {
  const titel = (v.titel || '').trim() || 'ohne Titel';
  return vorlageMedienLoeschbar(v)
    ? `Vorlage „${titel}" löschen? Sie war nie freigegeben, es gibt also keine Kundenkopien. Ihre Bilder und Videos werden mitgelöscht.`
    : `Vorlage „${titel}" löschen? Sie war schon einmal freigegeben. Gelöscht wird nur, wenn keine Kundenkopie und keine andere Unterweisung mehr auf sie oder ihre Dateien zeigt — das prüft die Datenbank. Ihre Bilder und Videos werden mitgelöscht.`;
};

export const FREIGEGEBEN_MELDUNG =
  'Die Vorlage ist freigegeben. Beim Kunden steht sie jetzt unter „Neue Unterweisung → Aus Vorlage" zur Wahl.';

export const KEIN_RECHT_HINWEIS = 'Nur-Ansicht: Bearbeiten dürfen Admins, Nutzer mit Häkchen „SiFa" und wer das Recht „SiFa-Vorlagen" hat.';

/**
 * „bei N Kunden" — gezaehlt werden Projekte mit Kopie (zaehleKundenJeVorlage),
 * soweit per RLS sichtbar; zwei Kopien beim selben Kunden zaehlen einmal.
 */
export const kopienText = (n: number): string => {
  const z = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  return z === 0 ? 'bei keinem Kunden' : `bei ${z} Kunden`;
};
